import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Button, Badge, Spinner } from "reactstrap";
import { usePostHog } from "@posthog/react";

import { fetchGameRound, submitGameAnswer } from "../hooks/useGame";
import { WINDOW_LABELS_LONG } from "../utils/windows";
import { analogStats, article, displayName, pct, pctOrDash, similarityPct } from "../utils/game";
import GameChart from "./GameChart";
import GameChartToggle, { useGameChartMode } from "./GameChartToggle";

/**
 * HomeGameTeaser — a single playable round of "Call the Chart" on the homepage.
 *
 * Deliberately one round, not the full loop: this is the hook into /game, so the reveal ends
 * with a link out rather than auto-loading the next chart (and the running tally, which only
 * means something over a run, is left to the game page).
 *
 * Equities only (`asset_class=equity`). The pool spans FX/crypto/futures too, but a first
 * impression of "$USDZAR" on the homepage of a Russell 3000 product reads as a different
 * product — and FX/crypto are the trend-contaminated classes whose directional base rate is
 * furthest from 50/50 (see the api repo's asset-class-stratification rule).
 *
 * The footer notes that rounds are drawn at random, never picked by outcome — that one is
 * load-bearing next to a "correct/wrong" verdict. No engine hit rate is shown here or on the
 * game page (`showWinRate={false}` on the fan chart there); that suppression stays opt-in.
 *
 * Presented as a play surface, not a figure: the panel carries a PLAY badge, the buttons are
 * the section's most prominent control, and the chart's reserved right side is itself
 * clickable (GameChart's call zones). Everything above it on the homepage is a chart to look
 * at, so without that treatment this one reads as another illustration and gets scrolled past.
 * No ↑/↓ shortcut here, unlike /game — swallowing arrow keys on a page people scroll through
 * costs more than the shortcut is worth.
 */
export default function HomeGameTeaser() {
  const posthog = usePostHog();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [round, setRound] = useState<GameBlindRound | null>(null);
  const [result, setResult] = useState<GameAnswerResult | null>(null);
  const [guess, setGuess] = useState<GameDirection | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [chartMode, setChartMode] = useGameChartMode();

  // Lazy: /api/game/round is `no-store`, so it can't be served from the CDN — and this is the
  // highest-traffic page on the site. Nobody pays for the round until they scroll to it.
  useEffect(() => {
    const el = hostRef.current;
    if (!el || visible) return;
    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let live = true;
    fetchGameRound({ assetClass: "equity" })
      .then(r => live && setRound(r))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [visible]);

  const answer = useCallback(
    async (direction: GameDirection) => {
      if (!round || result || submitting) return;
      setSubmitting(true);
      setGuess(direction);
      try {
        const res = await submitGameAnswer(round.round_token, direction);
        setResult(res);
        posthog.capture("game_round_answered", {
          window: round.window,
          asset_class: round.asset_class,
          correct: res.correct,
          engine_correct: res.engine_correct,
          surface: "home",
        });
      } catch {
        setGuess(null);
        setFailed(true);
      } finally {
        setSubmitting(false);
      }
    },
    [round, result, submitting, posthog],
  );

  // A dead teaser is worse than no teaser on the homepage — drop the whole section.
  if (failed) return null;

  const stats = analogStats(result?.analog_curve);
  const name = result ? displayName(result.ticker, result.ticker_name) : null;

  return (
    <section className="home-game-teaser py-5" ref={hostRef}>
      <Container>
        <Row className="justify-content-center">
          <Col lg={9}>
            <h2 className="section-title">Call the Chart</h2>
            <p className="text-center mb-4" style={{ color: "var(--text-muted)" }}>
              A real window from our signal archive, with the ticker and dates hidden. Call the next
              move — then see what actually happened, and what followed the charts that looked just
              like it.
            </p>

            {!round ? (
              <div className="text-center py-5">
                <Spinner size="sm" /> <span className="ms-2">Loading a chart…</span>
              </div>
            ) : (
              <div className="game-play-card">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="game-play-badge">▶ Play</span>
                    <Badge color="secondary" pill>
                      {WINDOW_LABELS_LONG[round.window] ?? `${round.window} bars`}
                    </Badge>
                  </div>
                  <GameChartToggle
                    mode={chartMode}
                    onChange={setChartMode}
                    available={Boolean(round.ohlc)}
                  />
                </div>
                <div className="mb-2" style={{ fontSize: 13, color: "var(--text-muted)" }}>
                  Where does it go over the next{" "}
                  {round.horizon === 1 ? "trading day" : `${round.horizon} trading days`}?
                </div>

                <GameChart
                  series={round.series}
                  ohlc={round.ohlc}
                  forward={result?.forward_series}
                  forwardOhlc={result?.forward_ohlc}
                  realized={result?.realized_direction}
                  mode={chartMode}
                  forwardPoints={round.horizon}
                  onCall={result || submitting ? undefined : direction => void answer(direction)}
                  height={260}
                />

                {!result ? (
                  <>
                    <div className="d-flex justify-content-center gap-3 mt-3">
                      <Button
                        color="success"
                        size="lg"
                        className="game-call-btn"
                        disabled={submitting}
                        onClick={() => void answer("up")}
                      >
                        ▲ Up
                      </Button>
                      <Button
                        color="danger"
                        size="lg"
                        className="game-call-btn"
                        disabled={submitting}
                        onClick={() => void answer("down")}
                      >
                        ▼ Down
                      </Button>
                    </div>
                    <div
                      className="text-center mt-2"
                      style={{ fontSize: 12, color: "var(--text-muted)" }}
                    >
                      or click the chart
                    </div>
                  </>
                ) : (
                  <div className="mt-3">
                    <div className="mb-2">
                      <strong>
                        You said {guess} — {result.correct ? "correct" : "not this time"}.
                      </strong>{" "}
                      <span style={{ color: "var(--text-muted)" }}>
                        {result.ticker}
                        {name ? ` (${name})` : ""} on {String(result.signal_date)} moved{" "}
                        {pct(result.realized_return)} over the next{" "}
                        {result.horizon === 1 ? "trading day" : `${result.horizon} trading days`}.
                      </span>
                    </div>

                    {stats && (
                      <p className="mb-2" style={{ fontSize: 13, color: "var(--text-muted)" }}>
                        {stats.count} windows in 20 years of history looked most like this one
                        {typeof result.top_similarity === "number" &&
                          ` — the closest is ${article(similarityPct(result.top_similarity))} ${similarityPct(result.top_similarity)}% shape match`}
                        . What followed them: median {pctOrDash(stats.median)}, middle half{" "}
                        {pctOrDash(stats.p25)} to {pctOrDash(stats.p75)}.
                      </p>
                    )}

                    <Link to="/game" className="btn btn-primary">
                      Play another chart →
                    </Link>
                  </div>
                )}
              </div>
            )}

            <p className="mt-4 mb-0" style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Charts are drawn at random from our signal archive — never picked by how they turned
              out.
            </p>
          </Col>
        </Row>
      </Container>
    </section>
  );
}
